import { Link } from '@tanstack/react-router';
import { ArrowRight, BookOpen } from 'lucide-react';
import { BUSINESS_TYPE_LABELS, ROLE_LABELS } from '@buildbee/contracts';
import { Badge, ErrorState, LoadingState, Status, formatDate, formatMoney } from '@buildbee/ui';
import { trpc } from '../../lib/trpc';
import { TaskRow } from '../crm/components/timeline';
import { statusLabel } from '../projects/lib';
import { statusTone } from '../status-tone';

/** Overdue and due-today tasks assigned to the signed-in user (hidden if CRM tasks aren't available). */
function NeedsYou() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const day = trpc.crm.tasks.myDay.useQuery({ timeZone, upcomingDays: 7 }, { retry: false });
  if (!day.data) return null;
  const tasks = [...day.data.tasks.overdue, ...day.data.tasks.today];
  return (
    <section aria-labelledby="needs-you" className="rounded-xl bg-card p-6 shadow-card">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="needs-you" className="text-xl font-semibold tracking-[-0.02em]">
          Needs you
        </h2>
        {tasks.length ? (
          <span className="text-[13px] text-muted-foreground">
            <span className="figures">{tasks.length}</span> {tasks.length === 1 ? 'item' : 'items'}
          </span>
        ) : null}
      </div>
      {tasks.length ? (
        <ul className="mt-3 divide-y">
          {tasks.slice(0, 6).map((t) => (
            <TaskRow key={t.id} task={t} />
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">Nothing overdue or due today.</p>
      )}
      <Link to="/crm" className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium hover:underline">
        Open my day <ArrowRight className="size-3.5" aria-hidden />
      </Link>
    </section>
  );
}

/** Active jobs with progress, from the regular project list. */
function ActiveProjects() {
  const list = trpc.projects.list.useQuery({ limit: 6, statuses: ['preconstruction', 'active', 'punch'] }, { retry: false });
  const rows = list.data?.items ?? [];
  if (!rows.length) return null;
  return (
    <section aria-labelledby="active-projects" className="overflow-hidden rounded-xl bg-card shadow-card">
      <div className="flex items-baseline justify-between gap-3 px-6 pb-2 pt-6">
        <h2 id="active-projects" className="text-xl font-semibold tracking-[-0.02em]">
          Projects
        </h2>
        <Link to="/projects" className="text-[13px] font-medium hover:underline">
          All projects
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm">
          <thead>
            <tr className="text-left text-[12px] text-muted-foreground">
              <th scope="col" className="px-6 py-2 font-medium">
                Project
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Progress
              </th>
              <th scope="col" className="px-6 py-2 text-right font-medium">
                Contract
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-border/70">
                <td className="px-6 py-3">
                  <Link to="/projects/$projectId" params={{ projectId: p.id }} className="font-semibold hover:underline">
                    {p.name}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    <span className="figures">{p.number}</span>
                    <Status tone={statusTone(p.status)} className="text-xs font-normal">
                      {statusLabel(p.status)}
                    </Status>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-full min-w-24 overflow-hidden rounded-full bg-muted" aria-hidden>
                      <div className="h-full rounded-full bg-foreground" style={{ width: `${p.percentComplete}%` }} />
                    </div>
                    <span className="figures w-10 text-right text-[13px]">{p.percentComplete}%</span>
                  </div>
                </td>
                <td className="figures px-6 py-3 text-right">{formatMoney(p.contractValueCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Points everyone at the in-app user guide (/help). */
function GuideCard() {
  return (
    <Link to="/help" className="group flex items-center gap-4 rounded-xl bg-card p-6 shadow-card transition-colors hover:bg-card/80">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-foreground">
        <BookOpen className="size-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[17px] font-semibold tracking-[-0.01em]">User guide and cheat sheet</span>
        <span className="block text-sm text-muted-foreground">Step-by-step help for CRM, projects, financials and cost codes.</span>
      </span>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden />
    </Link>
  );
}

export function HomePage() {
  const current = trpc.workspace.current.useQuery();
  const me = trpc.workspace.me.get.useQuery();
  if (current.isLoading) return <LoadingState />;
  if (current.error || !current.data) return <ErrorState error={current.error} onRetry={() => void current.refetch()} />;
  const ws = current.data.workspace;
  const firstName = me.data?.user.name.split(' ')[0];

  return (
    <div className="space-y-7">
      <header className="flex items-center justify-between gap-6">
        <div className="min-w-0 space-y-2">
          <p className="figures text-[13px] text-muted-foreground">{formatDate(new Date(), undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
          <h1 className="text-[32px] font-semibold leading-[1.1] tracking-[-0.03em] sm:text-[40px]">{firstName ? `Welcome back, ${firstName}` : 'Welcome'}</h1>
          <p className="inline-flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {ws.name} · {BUSINESS_TYPE_LABELS[ws.businessType]} <Badge>{ROLE_LABELS[current.data.membership.role]}</Badge>
            {ws.plan === 'trial' && ws.trialEndsAt ? (
              <Badge variant="warning">
                Trial ends <span className="figures">{formatDate(ws.trialEndsAt)}</span>
              </Badge>
            ) : null}
          </p>
        </div>
        {/* The company's own logo, when uploaded (Settings > Branding). */}
        {ws.logoUrl ? <img src={ws.logoUrl} alt={ws.name} className="h-14 w-auto max-w-[40%] shrink-0 object-contain sm:h-24 sm:max-w-[320px]" /> : null}
      </header>
      <NeedsYou />
      <ActiveProjects />
      <GuideCard />
    </div>
  );
}
